import type { EarnRule } from "./types";

type RateLike = Pick<EarnRule, "rate" | "unitAmount">;

/**
 * A card's base rate: the uncapped rule that applies to everything, as the
 * engine picks it (server/src/lib/rewards.ts, findBaseRule).
 */
export function baseRuleOf(rules: EarnRule[]): EarnRule | null {
  const today = new Date().toISOString().slice(0, 10);
  return (
    rules
      .filter(
        (r) =>
          !r.isExclusion &&
          r.multiplier == null &&
          r.qualifyingSpend == null &&
          r.capType === "NONE" &&
          r.categories.length === 0 &&
          r.merchantPatterns.length === 0 &&
          !(r.validFrom && r.validFrom.slice(0, 10) > today) &&
          !(r.validUntil && r.validUntil.slice(0, 10) < today),
      )
      .sort((a, b) => a.priority - b.priority)[0] ?? null
  );
}

/** The total multiple of the base a multiplier rule pays: "9X on top" is 10X. */
export function totalMultipleOf(rule: Pick<EarnRule, "multiplier" | "multiplierIncludesBase">): number | null {
  if (rule.multiplier == null) return null;
  return rule.multiplierIncludesBase === false ? rule.multiplier + 1 : rule.multiplier;
}

/** A rule's rate as it earns today: a multiplier read off the card's base rate. */
export function effectiveRate(rule: EarnRule, base: EarnRule | null): { rate: number; unitAmount: number } {
  const multiple = totalMultipleOf(rule);
  if (multiple === null) return { rate: rule.rate, unitAmount: rule.unitAmount };
  if (!base) return { rate: 0, unitAmount: rule.unitAmount };
  return { rate: Math.round(base.rate * multiple * 100) / 100, unitAmount: base.unitAmount };
}

/** "10X incl. base" or "+9X on top of base". */
export function describeMultiplier(rule: Pick<EarnRule, "multiplier" | "multiplierIncludesBase">): string | null {
  if (rule.multiplier == null) return null;
  const m = formatMultiple(rule.multiplier);
  return rule.multiplierIncludesBase === false ? `+${m} on top of base` : `${m} incl. base`;
}

/**
 * How many times the base rate a rule pays. A rule's rate is the total it
 * earns, base included: the engine uses the one matching rule in place of
 * the base rate, not on top of it. So 10X on a 5-per-₹150 card is 50 per ₹150.
 */
export function multipleOf(rule: RateLike, base: RateLike | null): number | null {
  if (!base || base.rate <= 0 || base.unitAmount <= 0 || rule.unitAmount <= 0) return null;
  return rule.rate / rule.unitAmount / (base.rate / base.unitAmount);
}

/** "10X", "2.5X". */
export function formatMultiple(multiple: number): string {
  const rounded = Math.round(multiple * 100) / 100;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(2).replace(/0$/, "")}X`;
}

/** 7.5 → "7.5", 7 → "7". */
function trim(n: number): string {
  return String(Math.round(n * 100) / 100);
}

/**
 * The plain-words reading of a rate against the card's base: "10X the base
 * rate — 5 base + 45 bonus per ₹150".
 */
export function describeRate(rule: RateLike, base: RateLike | null): string | null {
  const multiple = multipleOf(rule, base);
  if (!base || multiple === null) return null;
  const basePerUnit = (base.rate / base.unitAmount) * rule.unitAmount;
  const extra = rule.rate - basePerUnit;
  if (Math.abs(extra) < 1e-9) return `The same as the card's base rate (${trim(base.rate)} per ₹${trim(base.unitAmount)}).`;
  if (extra < 0) {
    return `Less than the card's base rate (${trim(base.rate)} per ₹${trim(base.unitAmount)}) — matching spends earn ${formatMultiple(multiple)} of normal.`;
  }
  return `${formatMultiple(multiple)} the base rate — ${trim(basePerUnit)} base + ${trim(extra)} bonus per ₹${trim(rule.unitAmount)}.`;
}
